import { RouterState } from 'connected-react-router';
import { combineReducers } from 'redux';
import { RatesModuleState } from '../modules/rates/types/rates.index.types';
import { AuthenticationState } from '../modules/authentication/types/authentication.types';
import { CoreModuleState } from '../modules/core/types';
import { SetupModuleState } from '../modules/setup/types/setup.index.types';
import { CarModelsState } from '../modules/setup/types/carmodel.types';
import { DeskState } from '../modules/setup/types/desk.types';
import { ProductState } from '../modules/setup/types/product.types';
import { BrokerRateState } from '../modules/setup/types/brokerrate.types';
import { ItemState } from '../modules/setup/types/item.types';
import { ClassifierState } from '../modules/admin/types/classifier.types';
import { DeskGroupState } from '../modules/setup/types/deskgroup.types';
import { SupplierInfoState } from '../modules/admin/types/supplierinfo.types';
import { carModelsReducer } from '../modules/setup/reducers/carmodel.reducers';
import { deskReducer } from '../modules/setup/reducers/desk.reducer';
import { productReducer } from '../modules/setup/reducers/product.reducer';
import { itemReducer } from '../modules/setup/reducers/item.reducer.';
import { brokerRateReducer } from '../modules/setup/reducers/brokerrate.reducer';
import { classifierReducer } from '../modules/admin/reducers/classifier.reducer';
import { deskGroupReducer } from '../modules/setup/reducers/deskgroup.reducer';
import { supplierInfoReducer } from '../modules/admin/reducers/supplierinfo.reducer';
import { itemPriceReducer } from '../modules/rates/reducers/itemprice.reducer';
import { promoCodesReducer } from '../modules/rates/reducers/promocode.reducer';
import { ratePlanReducer } from '../modules/rates/reducers/rateplan.reducer';
import { stopSellReducer } from '../modules/rates/reducers/stopsell.reducer';
import { ItemPriceState } from '../modules/rates/types/itemprice.types';
import { PromoCodesState } from '../modules/rates/types/promocode.types';
import { RatePlanState } from '../modules/rates/types/rateplan.types';
import { StopSellState } from '../modules/rates/types/stopsell.types';
import { ReservationState } from '../modules/reservations/types/reservation.types';
import { reservationReducer } from '../modules/reservations/reducers/reservation.reducer';
import { ReservationModuleState } from '../modules/reservations/types/reservation.index.types';
import { oneWayFeeReducer } from '../modules/rates/reducers/onewayfee.reducer';
import { OneWayFeesState } from '../modules/rates/types/onewayfees.types';
import { RateCacheStatusState } from '../modules/rates/types/rateCacheStatus.types';
import { rateCacheStatusReducer } from '../modules/rates/reducers/ratecachestatus.reducer';
import { deskOpeningReducer } from '../modules/setup/reducers/deskopening.reducer';
import { DeskOpeningState } from '../modules/setup/types/deskopening.types';
import { DeskOpeningExceptionState } from '../modules/setup/types/deskopeningexception.types';
import { deskOpeningExceptionReducer } from '../modules/setup/reducers/deskopeningexception.reducer';
import { onRequestRuleReducer } from '../modules/rates/reducers/onrequestrule.reducer';
import { OnRequestRuleState } from '../modules/rates/types/onrequestrule.types';
import { RatesRequestTestState } from '../modules/rates/types/ratesrequesttest.types';
import { ratesRequestTestReducer } from '../modules/rates/reducers/ratesrequesttest.reducer';
import { oneWayFeesItemPriceReducer } from '../modules/rates/reducers/onewayfeesitemprice.reducer';
import { DiscountRuleState } from '../modules/rates/types/discountrule.types';
import { discountRuleReducer } from '../modules/rates/reducers/discountrule.reducer';
import { NotificationState } from '../modules/admin/types/notification.types';
import { notificationReducer } from '../modules/admin/reducers/notification.reducer';
import { AdminModuleState } from '../modules/admin/types/admin.index.types';
import { BrokerState } from '../modules/admin/types/broker.types';
import { brokerReducer } from '../modules/admin/reducers/broker.reducer';
import { SupplierBrokerState } from '../modules/admin/types/supplierbroker.types';
import { supplierBrokerReducer } from '../modules/admin/reducers/supplierbroker.reducer';

export interface AppState {
  router: RouterState;
  rates: RatesModuleState;
  authentication: AuthenticationState;
  core: CoreModuleState;
  setup: SetupModuleState;
  reservation: ReservationModuleState;
  admin: AdminModuleState;
}

export const adminState: {
  notifications: NotificationState;
  classifiers: ClassifierState;
  brokers: BrokerState;
  supplierBrokers: SupplierBrokerState;
} = {
  notifications: {
    notifications: [],
    openNotifications: [],
    selectedNotification: null,
    postSuccess: null,
    lastFetch: null,
    totalElements: 0,
    totalPages: 1
  },
  classifiers: {
    classifiers: [],
    selectedClassifier: null,
    postSuccess: null,
    lastFetch: null,
    totalElements: 0,
    totalPages: 1
  },
  brokers: {
    brokers: [],
    selectedBroker: null,
    postSuccess: null,
    lastFetch: null,
    totalElements: 0,
    totalPages: 1
  },
  supplierBrokers: {
    supplierBrokers: [],
    selectedSupplierBroker: null,
    postSuccess: null,
    lastFetch: null,
    totalElements: 0,
    totalPages: 1
  }
};

export const adminReducer = combineReducers<AdminModuleState>({
  notifications: notificationReducer,
  classifiers: classifierReducer,
  brokers: brokerReducer,
  supplierBrokers: supplierBrokerReducer
});

export const setupState: {
  carModels: CarModelsState;
  desks: DeskState;
  products: ProductState;
  brokerRates: BrokerRateState;
  items: ItemState;
  deskGroups: DeskGroupState;
  supplierInfo: SupplierInfoState;
  deskOpenings: DeskOpeningState;
  deskOpeningExceptions: DeskOpeningExceptionState;
} = {
  carModels: {
    carModels: [],
    selectedCarModel: null,
    postSuccess: null,
    lastFetch: null,
    totalElements: 0,
    totalPages: 1,
    filters: null
  },
  desks: {
    desks: [],
    selectedDesk: null,
    postSuccess: null,
    lastFetch: null,
    totalElements: 0,
    totalPages: 1,
    filters: null
  },
  deskOpenings: {
    deskOpenings: [],
    selectedDeskOpening: null,
    postSuccess: null,
    lastFetch: null
  },
  deskOpeningExceptions: {
    deskOpeningExceptions: [],
    selectedDeskOpeningException: null,
    postSuccess: null,
    lastFetch: null
  },
  products: {
    products: [],
    selectedProduct: null,
    postSuccess: null,
    lastFetch: null,
    filters: null,
    totalElements: 0,
    totalPages: 1
  },
  brokerRates: {
    brokerRates: [],
    brokerRatesStatistics: [],
    selectedBrokerRate: null,
    postSuccess: null,
    lastFetch: null,
    totalPages: 1,
    totalElements: 0,
    filters: null
  },
  items: {
    items: [],
    selectedItem: null,
    postSuccess: null,
    lastFetch: null,
    totalElements: 0,
    totalPages: 1,
    filters: null
  },
  deskGroups: {
    deskGroups: [],
    selectedDeskGroup: null,
    postSuccess: null,
    lastFetch: null,
    totalElements: 0,
    totalPages: 1
  },
  supplierInfo: {
    selectedSupplierInfo: null,
    supplierInfo: null,
    lastFetch: null,
    postSuccess: null
  }
};

export const setupReducer = combineReducers<SetupModuleState>({
  carModels: carModelsReducer,
  desks: deskReducer,
  products: productReducer,
  items: itemReducer,
  brokerRates: brokerRateReducer,
  deskGroups: deskGroupReducer,
  supplierInfo: supplierInfoReducer,
  deskOpenings: deskOpeningReducer,
  deskOpeningExceptions: deskOpeningExceptionReducer
});

export const ratesState: {
  promoCodes: PromoCodesState;
  itemPrices: ItemPriceState;
  stopSell: StopSellState;
  ratePlans: RatePlanState;
  oneWayFees: OneWayFeesState;
  discountRules: DiscountRuleState;
  rateCacheStatus: RateCacheStatusState;
  onRequestRules: OnRequestRuleState;
  ratesRequestTest: RatesRequestTestState;
} = {
  promoCodes: {
    promoCodes: [],
    selectedPromoCode: null,
    postSuccess: null,
    lastFetch: null,
    totalElements: 0,
    totalPages: 1,
    filters: null
  },
  discountRules: {
    discountRules: [],
    selectedDiscountRule: null,
    postSuccess: null,
    lastFetch: null,
    totalElements: 0,
    totalPages: 1,
    filters: null
  },
  itemPrices: {
    itemPrices: [],
    selectedItemPrice: null,
    postSuccess: null,
    totalElements: 0,
    totalPages: 1,
    lastFetch: null,
    filters: null,
  },
  stopSell: {
    stopSells: [],
    selectStopSell: null,
    postSuccess: null,
    lastFetch: null,
    totalElements: 0,
    totalPages: 1,
    filters: null
  },
  ratePlans: {
    ratePlans: [],
    selectedRatePlan: null,
    postSuccess: null,
    lastFetch: null,
    productId: null,
    deskGroupId: null,
    acrissCodes: [],
    brokerRateCode: null,
    deskCode: null,
    startingDate: null
  },
  oneWayFees: {
    oneWayFees: [],
    oneWayFee: null,
    lastFetch: null
  },
  rateCacheStatus: {
    rateCacheStatus: null,
    coreCacheStatus: null
  },
  onRequestRules: {
    onRequestRules: [],
    selectedOnRequestRule: null,
    postSuccess: null,
    lastFetch: null,
    totalPages: 1,
    totalElements: 0,
    filters: null
  },
  ratesRequestTest: {
    filters: null
  }
};

export const ratesReducer = combineReducers<RatesModuleState>({
  promoCodes: promoCodesReducer,
  discountRules: discountRuleReducer,
  itemPrices: itemPriceReducer,
  stopSell: stopSellReducer,
  ratePlans: ratePlanReducer,
  oneWayFees: oneWayFeeReducer,
  oneWayFeeItemPrice: oneWayFeesItemPriceReducer,
  rateCacheStatus: rateCacheStatusReducer,
  onRequestRules: onRequestRuleReducer,
  ratesRequestTest: ratesRequestTestReducer
});

export const reservationsState: { reservations: ReservationState } = {
  reservations: {
    reservations: [],
    selectedReservation: null,
    postSuccess: null,
    lastFetch: null,
    totalElements: 0,
    totalPages: 1,
    filters: null
  }
};

export const reservationsReducer = combineReducers<ReservationModuleState>({
  reservations: reservationReducer
});
